import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StepNavigator from './StepNavigator';
import DentalArchDiagram from './DentalArchDiagram';

const REQUIREMENTS = [
  {
    label: 'Requirement 1',
    shortLabel: 'Req 1',
    title: 'Anterior Guidance',
    subtitle: 'Anterior teeth guide the mandible during protrusive movements',
    description:
      'During protrusion, the mandibular incisors glide along the palatal surfaces of the maxillary incisors. This anterior guidance should immediately disengage the posterior teeth, protecting them from horizontal forces.',
    leftDiagram: {
      title: 'Protrusive Movement',
      highlightedTeeth: ['UR1', 'UL1', 'UR2', 'UL2'],
      arrows: [
        { x1: 105, y1: 40, x2: 105, y2: 10, color: '#ef4444' },
        { x1: 96, y1: 40, x2: 96, y2: 10, color: '#ef4444' },
        { x1: 114, y1: 40, x2: 114, y2: 10, color: '#ef4444' },
      ],
      label: 'Anterior teeth guide protrusion',
    },
    rightDiagram: {
      title: 'Posterior Disclusion',
      highlightedTeeth: ['UR6', 'UR7', 'UL6', 'UL7'],
      arrows: [
        { x1: 62, y1: 95, x2: 62, y2: 115, color: '#3b82f6' },
        { x1: 148, y1: 95, x2: 148, y2: 115, color: '#3b82f6' },
      ],
      label: 'Posteriors should disengage',
    },
    guidance:
      'Verify that incisal contacts during protrusion create immediate posterior disclusion. Mark with articulating paper to confirm anterior guidance is functional.',
  },
  {
    label: 'Requirement 2',
    shortLabel: 'Req 2',
    title: 'Canine Guidance',
    subtitle: 'Canines guide lateral excursions on the working side',
    description:
      'During lateral excursion, the canine on the working side should bear the primary guiding contact. The canine\'s long root and favorable crown-to-root ratio make it ideal for accepting lateral forces. All posterior teeth on both sides should immediately disengage.',
    leftDiagram: {
      title: 'Right Lateral Excursion',
      highlightedTeeth: ['UR3'],
      arrows: [
        { x1: 72, y1: 52, x2: 55, y2: 42, color: '#ef4444' },
        { x1: 30, y1: 42, x2: 15, y2: 42, color: '#1f2937' },
      ],
      label: 'Right canine guides (working side)',
    },
    rightDiagram: {
      title: 'Left Lateral Excursion',
      highlightedTeeth: ['UL3'],
      arrows: [
        { x1: 138, y1: 52, x2: 155, y2: 42, color: '#ef4444' },
        { x1: 180, y1: 42, x2: 195, y2: 42, color: '#1f2937' },
      ],
      label: 'Left canine guides (working side)',
    },
    guidance:
      'Check canine guidance bilaterally with shimstock or articulating paper during lateral movements. The canine should be the last tooth to disengage on the working side.',
  },
  {
    label: 'Requirement 3',
    shortLabel: 'Req 3',
    title: 'Group Function',
    subtitle: 'Alternative to canine guidance — multiple working-side contacts',
    description:
      'When canine guidance is not achievable (worn canines, periodontal compromise, or Class II relationships), group function is an acceptable alternative. Multiple teeth on the working side share lateral forces. The key is that the balancing (non-working) side must still be free of contacts.',
    leftDiagram: {
      title: 'Group Function (Working Side)',
      highlightedTeeth: ['UR3', 'UR4', 'UR5', 'UR6'],
      arrows: [
        { x1: 50, y1: 80, x2: 35, y2: 70, color: '#ef4444' },
        { x1: 30, y1: 105, x2: 15, y2: 95, color: '#1f2937' },
      ],
      label: 'Multiple teeth share working-side forces',
    },
    rightDiagram: {
      title: 'Balancing Side (No Contact)',
      highlightedTeeth: [],
      arrows: [
        { x1: 148, y1: 105, x2: 165, y2: 115, color: '#3b82f6' },
        { x1: 155, y1: 130, x2: 172, y2: 140, color: '#3b82f6' },
      ],
      contactMarkers: [],
      label: 'No balancing-side contacts allowed',
    },
    guidance:
      'When using group function, ensure smooth, simultaneous contacts on the working side with immediate disclusion on the balancing side. Avoid isolated contacts that could create pivot points.',
  },
  {
    label: 'Requirement 4',
    shortLabel: 'Req 4',
    title: 'No Balancing Side Contacts',
    subtitle: 'The non-working side must be free of all contacts during lateral excursion',
    description:
      'Balancing (non-working) side interferences are the most destructive type of occlusal interference. During lateral excursion, the condyle on the balancing side moves downward and forward. Any tooth contact on this side creates a lever effect that can damage the TMJ, cause muscle hyperactivity, and accelerate tooth wear.',
    leftDiagram: {
      title: 'Working Side (Right)',
      highlightedTeeth: ['UR3', 'UR4', 'UR5'],
      arrows: [
        { x1: 50, y1: 60, x2: 35, y2: 50, color: '#ef4444' },
      ],
      label: 'Guided contacts (acceptable)',
    },
    rightDiagram: {
      title: 'Balancing Side (Left)',
      highlightedTeeth: ['UL6', 'UL7'],
      arrows: [
        { x1: 148, y1: 95, x2: 165, y2: 85, color: '#ef4444' },
        { x1: 155, y1: 120, x2: 172, y2: 110, color: '#ef4444' },
      ],
      contactMarkers: [
        { x: 148, y: 105, color: '#ef4444' },
        { x: 155, y: 130, color: '#ef4444' },
      ],
      label: 'Interference! Must eliminate',
    },
    guidance:
      'Use articulating paper and shimstock to check for balancing-side contacts. These must be eliminated through occlusal adjustment, restorations, or orthodontics. This is the single most important occlusal check.',
  },
  {
    label: 'Requirement 5',
    shortLabel: 'Req 5',
    title: 'Stable Centric Stops',
    subtitle: 'Even bilateral simultaneous contacts in centric relation',
    description:
      'In centric relation (or maximum intercuspation), all posterior teeth should contact simultaneously with equal intensity. Centric stops should be on flat surfaces perpendicular to the long axis of each tooth. This distributes occlusal forces axially and provides a stable, repeatable mandibular position.',
    leftDiagram: {
      title: 'Even Bilateral Contacts',
      highlightedTeeth: ['UR4', 'UR5', 'UR6', 'UR7', 'UL4', 'UL5', 'UL6', 'UL7'],
      arrows: [],
      contactMarkers: [
        { x: 66, y: 80, color: '#22c55e' },
        { x: 68, y: 60, color: '#22c55e' },
        { x: 62, y: 105, color: '#22c55e' },
        { x: 55, y: 130, color: '#22c55e' },
        { x: 144, y: 80, color: '#22c55e' },
        { x: 142, y: 60, color: '#22c55e' },
        { x: 148, y: 105, color: '#22c55e' },
        { x: 155, y: 130, color: '#22c55e' },
      ],
      label: 'Simultaneous bilateral contacts',
    },
    rightDiagram: {
      title: 'Premature Contact (Problem)',
      highlightedTeeth: ['UR6'],
      arrows: [
        { x1: 62, y1: 90, x2: 62, y2: 115, color: '#ef4444' },
      ],
      contactMarkers: [
        { x: 62, y: 105, color: '#ef4444' },
      ],
      label: 'Single premature contact — unstable',
    },
    guidance:
      'Use bimanual manipulation to seat the condyles and verify simultaneous bilateral contacts with shimstock. A single premature contact can cause the mandible to deflect, leading to muscle splinting, TMD symptoms, or tooth mobility.',
  },
];

const TwoDChecks = () => {
  const [activeStep, setActiveStep] = useState(0);
  const req = REQUIREMENTS[activeStep];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Back link */}
      <Link
        to="/patients"
        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mb-4"
      >
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to patient management
      </Link>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">2D Checks</h1>
        <p className="text-gray-500 mt-1">5 Requirements for Stable Occlusion</p>
      </div>

      {/* Step Navigator */}
      <div className="mb-6">
        <StepNavigator
          steps={REQUIREMENTS}
          activeStep={activeStep}
          onStepChange={setActiveStep}
        />
      </div>

      {/* Content card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Title bar */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">{req.title}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{req.subtitle}</p>
        </div>

        {/* Description */}
        <div className="px-6 py-4 border-b border-gray-100">
          <p className="text-sm text-gray-700 leading-relaxed">{req.description}</p>
        </div>

        {/* Diagrams side by side */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-lg p-4">
              <DentalArchDiagram
                title={req.leftDiagram.title}
                highlightedTeeth={req.leftDiagram.highlightedTeeth}
                arrows={req.leftDiagram.arrows}
                contactMarkers={req.leftDiagram.contactMarkers}
                label={req.leftDiagram.label}
              />
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <DentalArchDiagram
                title={req.rightDiagram.title}
                highlightedTeeth={req.rightDiagram.highlightedTeeth}
                arrows={req.rightDiagram.arrows}
                contactMarkers={req.rightDiagram.contactMarkers}
                label={req.rightDiagram.label}
              />
            </div>
          </div>
        </div>

        {/* Clinical guidance */}
        <div className="px-6 py-4 bg-blue-50 border-t border-blue-100">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-blue-800">Clinical Guidance</h3>
              <p className="text-sm text-blue-700 mt-1 leading-relaxed">{req.guidance}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Step indicator */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-400">
          Requirement {activeStep + 1} of {REQUIREMENTS.length}
        </p>
      </div>
    </div>
  );
};

export default TwoDChecks;
